import { useNavigate } from "react-router-dom";
import { clientUpdateProfile, changePass } from "../Api/SubUrl";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CallApiGetUserHistories,
  CallApiGetVoucherValidation,
  CallApiSendRequestDeleteAccount,
  CallApiGetUserMisaInvoiceView,
  CallApiDownloadInvoice,
} from "../Api/Enforcement";
import {
  ShowCustomConfirmModal,
  ShowCustomErrorModal,
} from "../Components/Atoms/CustomErrorModal";
import { SwalError } from "../Share/Swal";
import { formatDay } from "../Utils";
import { MdOutlineVisibility, MdOutlineFileDownload } from "react-icons/md";

export const ONE_MILLION_UNIT = 1000000;

const getDeleteAccountRequestKey = (accountName) =>
  `deleteAccountRequested:${accountName}`;

export default function useMDProfile({ showProfile, data, introduceId }) {
  const navigate = useNavigate();
  const [introducedHistories, setIntroducedHistories] = useState([]);
  const [voucherHistories, setVoucherHistories] = useState([]);
  const [totalBonus, setTotalBonus] = useState(0);
  const baseUrl = `https://img.vietqr.io/image/${process.env.REACT_APP_BANK_ID}-${process.env.REACT_APP_ACCOUNT_NO}-qr_only.jpg?addInfo=SEVQR%20`;

  // State definitions
  const [qrCode, setQrCode] = useState("");
  const [qrNormal, setQrNormal] = useState("");
  const [qrNormalAmount, setQrNormalAmount] = useState({
    value: "100000",
    formatValue: "100.000",
  });
  const [qrNormalError, setQrNormalError] = useState("");
  const [voucherText, setVoucherText] = useState("");
  const [voucher, setVoucher] = useState();
  const [input, setInput] = useState("");
  const [qrUserId, setQrUserId] = useState(data?.AccountName || "");
  const [copiedText, setCopiedText] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [example, setExample] = useState(null);
  const [amount, setAmount] = useState({ value: "0", formatValue: "0" });
  const [showNewVersion, setShowNewVersion] = useState(false);
  const [hasDeleteRequest, setHasDeleteRequest] = useState(
    () =>
      !!data?.AccountName &&
      localStorage.getItem(getDeleteAccountRequestKey(data.AccountName)) === "1"
  );
  const [isSubmittingDelete, setIsSubmittingDelete] = useState(false);

  // Sử dụng useRef để theo dõi các giá trị hiện tại
  const stateRef = useRef({
    amount: { value: "0", formatValue: "0" },
    showNewVersion: false,
    normalAmount: { value: "100000", formatValue: "100.000" },
    qrUserId: data?.AccountName || "",
  });

  // Cập nhật ref khi state thay đổi
  useEffect(() => {
    stateRef.current.amount = amount;
  }, [amount]);

  useEffect(() => {
    stateRef.current.normalAmount = qrNormalAmount;
  }, [qrNormalAmount]);

  useEffect(() => {
    stateRef.current.showNewVersion = showNewVersion;
  }, [showNewVersion]);

  useEffect(() => {
    stateRef.current.qrUserId = qrUserId;
  }, [qrUserId]);

  // Hàm định dạng số với dấu phẩy
  const formatNumberWithCommas = useCallback((value) => {
    if (!value) return "";
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }, []);

  const resolveQrUserId = useCallback(
    (customUserId) => {
      const resolved = (customUserId || data?.AccountName || "")
        .toLowerCase()
        .trim();
      setQrUserId(resolved);
      stateRef.current.qrUserId = resolved;
      return resolved;
    },
    [data]
  );

  // Verify ID tồn tại qua voucher-validation (type Introduce)
  const verifyQrUserId = useCallback((userId, onSuccess, onError) => {
    const param = {
      VoucherCode: userId.toLowerCase().trim(),
    };

    CallApiGetVoucherValidation(param, (res) => {
      const normalizedId = userId.toLowerCase().trim();

      if (res && res.type === "Introduce") {
        onSuccess(normalizedId);
        return;
      }

      onError(`ID ${normalizedId} không hợp lệ`);
    });
  }, []);

  // Xử lý tạo QR khuyến mãi (mã KM luôn là "km", ID lấy từ input nếu có)
  const handleGenerateQr = useCallback(
    (customUserId) => {
      if (!data?.AccountName) return;

      setLoading(true);
      const voucherCode = "km";
      const userId = resolveQrUserId(customUserId);
      const param = {
        VoucherCode: voucherCode,
      };

      CallApiGetVoucherValidation(param, (res) => {
        setLoading(false);
        if (res && res.type && res.voucher) {
          const currentAmount = stateRef.current.amount.value;
          const isNewVersion = stateRef.current.showNewVersion;
          const minAmount = res.voucher.minPay * ONE_MILLION_UNIT;

          // Kiểm tra số tiền tối thiểu
          if (isNewVersion && minAmount > currentAmount) {
            setError(
              `Cần thêm tối thiểu ${formatNumberWithCommas(
                minAmount
              )} VNĐ để sử dụng mã khuyến mãi`
            );

            const newAmountState = {
              value: `${minAmount}`,
              formatValue: formatNumberWithCommas(minAmount),
            };

            setAmount(newAmountState);
            stateRef.current.amount = newAmountState;
            return;
          }

          // Tạo QR code URL
          const amountText =
            isNewVersion && currentAmount !== "0"
              ? `&amount=${currentAmount}`
              : "";

          const qr = `${baseUrl}${userId}%20${voucherCode}%20SEVQR%20${amountText}`;
          setQrCode(qr);

          setVoucher(res.voucher);
          setVoucherText(`Mã khuyến mãi: ${voucherCode}`);
          setExample(res.example);
          setError("");
        } else {
          if (res.Status && res.Status.status === 400) {
            setError(res.Status.data.Message);
          } else {
            SwalError(
              "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ"
            );
          }
        }
      });
    },
    [data, baseUrl, formatNumberWithCommas, resolveQrUserId]
  );

  // Xử lý tạo QR thường
  const handleGenerateNormalQR = useCallback(
    (customUserId) => {
      if (!data?.AccountName && !customUserId && !stateRef.current.qrUserId) {
        return;
      }

      const idOverride =
        typeof customUserId === "string"
          ? customUserId
          : stateRef.current.qrUserId;
      const userId = resolveQrUserId(idOverride || undefined);
      const isNewVersion = stateRef.current.showNewVersion;

      // Chế độ mặc định: QR không gắn số tiền
      if (!isNewVersion) {
        const qr = `${baseUrl}${userId}%20SEVQR`;
        setQrNormal(qr);
        setQrNormalError("");
        return;
      }

      const normalAmount = stateRef.current.normalAmount.value;

      // Check for minimum amount
      if (normalAmount && parseInt(normalAmount) < 5000) {
        setQrNormalError("Giá trị tối thiểu là 5.000 VNĐ");
        return;
      }

      const amountText =
        normalAmount && parseInt(normalAmount) > 0
          ? `&amount=${normalAmount}`
          : "";
      const qr = `${baseUrl}${userId}%20SEVQR%20${amountText}`;
      setQrNormal(qr);
      setQrNormalError("");
    },
    [baseUrl, data, resolveQrUserId]
  );

  const handleGenerateDefeaultNormalQR = useCallback(
    (customUserId) => {
      const idOverride =
        typeof customUserId === "string" ? customUserId : undefined;
      const userId = resolveQrUserId(idOverride || undefined);
      const qr = `${baseUrl}${userId}%20SEVQR`;
      setQrNormal(qr);
      stateRef.current.normalAmount = {
        value: "100000",
        formatValue: "100.000",
      };
      setQrNormalAmount({ value: "100000", formatValue: "100.000" });
      setQrNormalError("");
    },
    [baseUrl, resolveQrUserId]
  );

  // Xử lý tạo QR mặc định
  const handleGenerateDefaultQR = useCallback(() => {
    setInput("");
    handleGenerateQr();
    handleGenerateDefeaultNormalQR("");
  }, [handleGenerateQr, handleGenerateDefeaultNormalQR]);

  const handleNormalAmountChange = useCallback(
    (e) => {
      const value = e.target.value.replace(/[^\d]/g, "");

      const formattedValue = formatNumberWithCommas(value);
      const newAmountState = { value: value, formatValue: formattedValue };
      setQrNormalAmount(newAmountState);
      stateRef.current.normalAmount = newAmountState;
    },
    [formatNumberWithCommas]
  );

  // Xử lý thay đổi phiên bản
  const handleChangeVersion = useCallback(
    (newVersion) => {
      // Cập nhật state và ref trước
      stateRef.current.showNewVersion = newVersion;
      setShowNewVersion(newVersion);

      // Dùng ID đã verify gần nhất, không dùng raw input chưa kiểm tra
      const userId = stateRef.current.qrUserId || undefined;

      // Xử lý amount dựa trên newVersion
      if (!newVersion) {
        setAmount({
          value: "0",
          formatValue: "0",
        });
        handleGenerateDefeaultNormalQR(userId || "");
      } else if (voucher && voucher.minPay) {
        const minAmount = voucher.minPay * ONE_MILLION_UNIT;
        const newAmountState = {
          value: `${minAmount}`,
          formatValue: formatNumberWithCommas(minAmount),
        };

        setAmount(newAmountState);
        stateRef.current.amount = newAmountState;
        handleGenerateNormalQR(userId || "");
      } else {
        handleGenerateNormalQR(userId || "");
      }

      handleGenerateQr(userId);
    },
    [
      voucher,
      formatNumberWithCommas,
      handleGenerateQr,
      handleGenerateDefeaultNormalQR,
      handleGenerateNormalQR,
    ]
  );

  // Xử lý thay đổi giá trị số tiền
  const handleAmountChange = useCallback(
    (e) => {
      const value = e.target.value.replace(/[^\d]/g, ""); // Chỉ cho nhập số

      const formattedValue = formatNumberWithCommas(value);
      const newAmountState = { value: value, formatValue: formattedValue };
      setAmount(newAmountState);
      stateRef.current.amount = newAmountState;
      setError("");
    },
    [formatNumberWithCommas]
  );

  useEffect(() => {

    if (!showProfile || !data?.AccountName) {
      setIntroducedHistories([]);
      setVoucherHistories([]);
      setTotalBonus(0);
      return;
    }

    const param = {
      Page: 0,
      PageSize: 5,
      DateFrom: null,
      DateTo: null,
    };

    CallApiGetUserHistories(param, (histRes) => {
      if (
        histRes &&
        histRes.ListWithRefRate &&
        histRes.ListWithoutRefRate &&
        typeof histRes.TotalBonus === "number"
      ) {
        setIntroducedHistories(histRes.ListWithRefRate.Items);
        setVoucherHistories(histRes.ListWithoutRefRate.Items);
        setTotalBonus(histRes.TotalBonus);
        return;
      }

      ShowCustomErrorModal({
        message: "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ",
        title: "Đã xảy ra lỗi",
      });
    });
  }, [showProfile, data?.AccountName]);

  useEffect(() => {
    if (!data?.AccountName) return;

    handleGenerateDefaultQR();
  }, [data?.AccountName, handleGenerateDefaultQR]);

  // Xử lý submit: input = ID trong QR; trống = dùng ID đăng nhập (giữ mã km)
  const handleSubmit = useCallback(() => {
    if (loading || !data?.AccountName) return;

    const normalizedInput = (input || "").toLowerCase().trim();
    const ownId = (data?.AccountName || "").toLowerCase().trim();

    // Trống hoặc trùng ID đăng nhập → QR mặc định, không cần verify
    if (!normalizedInput || normalizedInput === ownId) {
      setError("");
      handleGenerateQr(normalizedInput || undefined);
      handleGenerateNormalQR(normalizedInput || "");
      return;
    }

    setLoading(true);
    setError("");

    verifyQrUserId(
      normalizedInput,
      (verifiedId) => {
        setLoading(false);
        handleGenerateQr(verifiedId);
        handleGenerateNormalQR(verifiedId);
      },
      (message) => {
        setLoading(false);
        setError(message);
        setQrCode("");
        setQrNormal("");
      }
    );
  }, [
    loading,
    input,
    data,
    verifyQrUserId,
    handleGenerateQr,
    handleGenerateNormalQR,
  ]);

  const handleChangePass = useCallback(() => {
    navigate(changePass);
  }, [navigate]);

  const handleUpdate = useCallback(() => {
    navigate(clientUpdateProfile);
  }, [navigate]);

  useEffect(() => {
    if (!data?.AccountName) {
      setHasDeleteRequest(false);
      return;
    }

    setHasDeleteRequest(
      localStorage.getItem(getDeleteAccountRequestKey(data.AccountName)) === "1"
    );
  }, [data?.AccountName]);

  const handleDeleteAccount = useCallback(async () => {
    if (hasDeleteRequest || isSubmittingDelete) return;

    const result = await ShowCustomConfirmModal({
      title: "Xóa tài khoản",
      message: "Bạn chắc chắn muốn gửi yêu cầu xóa tài khoản?",
    });
    if (!result.isConfirmed) return;

    setIsSubmittingDelete(true);
    CallApiSendRequestDeleteAccount((res) => {
      setIsSubmittingDelete(false);
      if (res === "success") {
        setHasDeleteRequest(true);
        if (data?.AccountName) {
          localStorage.setItem(
            getDeleteAccountRequestKey(data.AccountName),
            "1"
          );
        }
        return;
      }

      const error = res.Status;
      ShowCustomErrorModal({
        title: "Đã xảy ra lỗi",
        message:
          error?.status === 400
            ? error.data.Message
            : "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ",
      });
    });
  }, [data?.AccountName, hasDeleteRequest, isSubmittingDelete]);

  const handleInput = useCallback((e) => {
    const value = e.target.value.toLowerCase();
    setInput(value.trim());
  }, []);

  const viewingIdRef = useRef(null);
  const downloadingIdRef = useRef(null);

  const handleViewDetail = useCallback((row) => {
    if (!row?.Id || viewingIdRef.current != null) return;

    viewingIdRef.current = row.Id;
    const tab = window.open("", "_blank");

    CallApiGetUserMisaInvoiceView(row.Id, (res) => {
      viewingIdRef.current = null;

      if (res?.Data) {
        if (tab) {
          tab.location.href = res.Data;
        } else {
          window.open(res.Data, "_blank");
        }
        return;
      }

      if (tab) tab.close();

      const error = res.Status;
      ShowCustomErrorModal({
        title: "Đã xảy ra lỗi",
        message:
          error?.status === 400
            ? error.data.Message
            : "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ",
      });
    });
  }, []);

  const handleDownload = useCallback((row) => {
    if (!row?.Id || downloadingIdRef.current != null) return;

    downloadingIdRef.current = row.Id;
    CallApiDownloadInvoice(row.Id, (res) => {
      downloadingIdRef.current = null;

      if (res instanceof Blob) {
        const objectUrl = window.URL.createObjectURL(res);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = `${row.Mi_TransactionId || row.Id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
        return;
      }

      const error = res.Status;
      ShowCustomErrorModal({
        title: "Đã xảy ra lỗi",
        message:
          error?.status === 400
            ? error.data.Message
            : "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ",
      });
    });
  }, []);

  const voucherHistoryColumns = [
    {
      header: "Mã giao dịch",
      key: "Se_TransactionId",
    },
    // {
    //   header: "Loại",
    //   key: "VoucherCode",
    //   render: (row) => (row.VoucherCode ? "KM" : "Thường"),
    // },
    { header: "KNB", key: "nExtPoint1" },
    { header: "KNB Khóa", key: "nExtPoint2" },
    { header: "Gói", 
      key: "VoucherCode", 
      render: (row) => (row.VoucherCode ? "Khuyến mãi" : "Thường"),
    },
    {
      header: "Thời gian",
      key: "Create_Date",
      render: (row) => formatDay(row.Create_Date),
    },
    // { header: "Mã", key: "VoucherCode" },
    // {
    //   header: "Mức KM 1",
    //   key: "limit1",
    //   render: (row) =>
    //     row.limit1 ? formatNumber(row.limit1 * ONE_MILLION_UNIT) : "",
    // },
    // { header: "Tỉ lệ 1", key: "ex_rate1" },
    // {
    //   header: "Mức KM 2",
    //   key: "limit2",
    //   render: (row) =>
    //     row.limit2 ? formatNumber(row.limit2 * ONE_MILLION_UNIT) : "",
    // },
    // { header: "Tỉ lệ 2", key: "ex_rate2" },
    {
      header: "Thể loại",
      key: "ex_rate2",
      render: () => "Quy đổi",
    },
    {
      header: "Trạng thái",
      key: "ex_rate2",
      render: () => "Thành công",
    },
    {
      header: "Xem hóa đơn",
      key: "detail",
      align: "center",
      render: (row) => (
        <MdOutlineVisibility
          size={18}
          style={{ cursor: "pointer" }}
          onClick={() => handleViewDetail(row)}
        />
      ),
    },
    {
      header: "Tải hóa đơn",
      key: "download",
      align: "center",
      render: (row) => (
        <MdOutlineFileDownload
          size={18}
          style={{ cursor: "pointer" }}
          onClick={() => handleDownload(row)}
        />
      ),
    },
  ];

  const introducedHistoryColumns = [
    // {
    //   header: "Người thêm",
    //   key: "UserCreate",
    // },
    { header: "KNB thưởng", key: "nExtPoint1" },
    { header: "KNB Khóa thưởng", key: "nExtPoint2" },
    {
      header: "Ngày thêm",
      key: "Create_Date",
      render: (row) => formatDay(row.Create_Date),
    },
  ];

  const handleCopy = useCallback(
    async (isCopyId) => {
      try {
        const content = isCopyId
          ? data.AccountName
          : `${process.env.REACT_APP_FRONTEND_URL}/login?introduceId=${data.AccountName}`;

        await navigator.clipboard.writeText(content);
        setCopiedText(content);

        // Reset trạng thái sau 10 giây
        setTimeout(() => setCopiedText(""), 10000);
      } catch (error) {
        console.error("Không thể copy vào clipboard:", error);
      }
    },
    [data]
  );

  return {
    // State
    loading,
    voucherText,
    user: data,
    qrCode,
    error,
    input,
    qrUserId,
    voucher,
    totalBonus,
    example,
    amount,
    showNewVersion,
    copiedText,
    voucherHistories,
    introducedHistories,
    qrNormalAmount,
    qrNormal,
    qrNormalError,
    hasDeleteRequest,
    isSubmittingDelete,

    // Handlers
    handleChangePass,
    handleUpdate,
    handleDeleteAccount,
    handleSubmit,
    handleGenerateDefaultQR,
    handleInput,
    handleCopy,
    handleAmountChange,
    handleChangeVersion,
    handleNormalAmountChange,
    handleGenerateNormalQR,

    // Columns
    voucherHistoryColumns,
    introducedHistoryColumns,
  };
}
